import ProductFlags from '@jo-v1/components/ProductFlags';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import JoTooltip from '@core/blocks/jo-v1/features/JoTooltip';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
import {
  selectHeclidMap,
  selectProductCorrelationIdMap,
  selectRecommenderPopupListId,
  setViewCorrelationIdMapEntry,
  setProductCorrelationIdMapEntry,
} from '@core/reducers/eventingSlice';
import {
  selectSubmissions,
  setLeadDelivery,
  addSubmission,
  setIsComparisonModalOpen,
} from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta } from '@core/services/mobius';
import type { VoyagerResult } from '@core/ts/results';
import cn from '@core/utils/classNames';

import styles from './Card.module.css';

type Props = {
  result: VoyagerResult;
  position: number;
  eventingOverrides?: Record<string, any>;
  refSetter?: (node: HTMLDivElement | null) => void;
  squareButtons?: boolean;
};

const Card: FC<Props> = ({ result, position, eventingOverrides, refSetter, squareButtons }) => {
  const dispatch = useDispatch();

  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: 'compare-voyager',
      formatType: 'compare-modal',
      formatSubtype: 'app',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  // Fire productLoaded + productViewed events and adds entry to the viewCorrelationIdMap
  useEffect(() => {
    productLoaded();
    productViewed();
    if (result?.program?.id) {
      dispatch(
        setViewCorrelationIdMapEntry({
          key: result.program.id,
          value: newViewCorrelationId,
        })
      );
    }
  }, []);

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result.school.id);
  const handleProductClick = () => {
    dispatch(setIsComparisonModalOpen(false));
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(addSubmission({ ...result, duplicate: true }));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  return (
    <div className={styles.card} key={result?.program?.id}>
      <div ref={refSetter} className={styles.cardTop}>
        <div>
          <img
            className={styles.schoolLogo}
            src={
              result?.school?.slug
                ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                : undefined
            }
            alt={result?.school?.name}
          />
          <h3>{result?.school?.name}</h3>
          <h4>{result?.program?.name}</h4>
          <div className={styles.tagsContainer}>
            <ProductFlags flags={result?.school?.additional} withBullets={false} />
          </div>
        </div>

        {!isProgramSubmitted ? (
          <button
            onClick={handleProductClick}
            className={cn(styles.learnMoreButton, squareButtons && styles.squareButton)}
            type="button"
          >
            Learn More
          </button>
        ) : (
          <button
            onClick={
              applicationCtaData?.url ? () => window.open(applicationCtaData?.url, '_blank') : handleProductClick
            }
            type="button"
            className={cn(styles.cardBtnCtaSubmitted, squareButtons && styles.squareButton)}
          >
            {applicationCtaData?.label || 'Next Steps'}
          </button>
        )}
      </div>
      <ul className={styles.schoolInfoList}>
        <hr className={styles.divider} />
        <li>
          <span>
            Online Enrollment
            <JoTooltip
              fitParent
              text={
                <>
                  <h4>Online Enrollment</h4>
                  Percentage of all students who are enrolled online.
                </>
              }
            />
          </span>
          <strong>
            {result?.school?.additional?.percentOnlineEnrollment
              ? `${Math.round(Number(result?.school?.additional?.percentOnlineEnrollment))}%`
              : 'N/A'}
          </strong>
        </li>
        <li>
          <span>
            Accreditation
            <JoTooltip
              fitParent
              text={
                <>
                  <h4>Accreditation</h4>
                  {result?.school?.additional?.accreditator}
                </>
              }
            />
          </span>
          <strong>{result?.school?.additional?.accreditator ? 'Yes' : 'N/A'}</strong>
        </li>
        <li>
          <span>Online Programs</span>
          <strong>
            {result?.school?.additional?.numberOnlinePrograms
              ? result?.school?.additional?.numberOnlinePrograms
              : 'N/A'}
          </strong>
        </li>
        <li>
          <span>Retention Rate</span>
          <strong>
            {result?.school?.additional?.fulltimeRetentionRate
              ? `${Math.round(Number(result?.school?.additional?.fulltimeRetentionRate))}%`
              : 'N/A'}
          </strong>
        </li>
        <li>
          <span>Transfer Credits Accepted</span>
          <strong>
            {(() => {
              const accepted = result?.school?.additional?.areTransferCreditsAccepted;
              if (accepted === undefined) return 'N/A';
              if (accepted) return 'Yes';
              return 'No';
            })()}
          </strong>
        </li>
        <li>
          <span>Online Application Fee Waived</span>
          <strong>
            {(() => {
              const waived = result?.school?.additional?.isApplicationFeeOnlineWaived;
              if (waived === undefined) return 'N/A';
              if (waived) return 'Yes';
              return 'No';
            })()}
          </strong>
        </li>
        <li>
          <span>SAT/ACT Required</span>
          <strong>{result?.school?.additional?.satOrActAdmissionsRequirements ?? 'N/A'}</strong>
        </li>
      </ul>
      <p className={styles.programSnippet}>{result?.program?.snippet}</p>
      <div className={styles.bottomCta}>
        {!isProgramSubmitted ? (
          <button
            onClick={handleProductClick}
            className={cn(styles.learnMoreButton, squareButtons && styles.squareButton)}
            type="button"
          >
            Learn More
          </button>
        ) : (
          <button
            onClick={
              applicationCtaData?.url ? () => window.open(applicationCtaData?.url, '_blank') : handleProductClick
            }
            type="button"
            className={cn(styles.cardBtnCtaSubmitted, squareButtons && styles.squareButton)}
          >
            {applicationCtaData?.label || 'Next Steps'}
          </button>
        )}
      </div>
    </div>
  );
};

export default Card;
